import type { AgenticServerBase } from "./server.ts";

/** Define a listable field */
type AccessFieldSpec = {
  /** Field description */
  description: string;
  /** Other fields that must be passed to list successfully (e.g. 'account' when listing AWS roles) */
  required: string[];
  /** Prompt sent to the agent to use listing */
  prompt?: string;
};

/** One access type served by a server that serves several
 *
 * P0 CLI access requests are shaped `p0 request <resource-type> <access-type> ...`.
 * A server that serves a single access type pins both positionals in its
 * `prefixArgs`; one that serves several keeps only what is common there (the
 * resource type) and supplies the rest per access type here.
 */
type AccessTypeSpec = {
  /** Prompt sent to the agent describing how to form request arguments for this
   *  access type specifically; the server-level `access.prompt` covers what is
   *  common to every access type */
  prompt: string;
  /** Arguments appended after `access.prefixArgs` when this access type is selected */
  args: string[];
};

/** The listable fields belonging to one access type */
type ListAccessTypeSpec = {
  /** Arguments appended after `list.prefixArgs` when this access type is selected */
  args: string[];
  /** Fields listable for this access type */
  fields: Record<string, AccessFieldSpec>;
};

/** JSON Schema describing an MCP tool's expected input parameters */
type McpToolInputSchema = {
  type: "object";
  properties?: Record<string, unknown>;
  required?: string[];
  additionalProperties?: boolean;
};

/** JSON Schema describing an MCP tool's expected output structure */
type McpToolOutputSchema = {
  type: "object";
  properties?: Record<string, unknown>;
  required?: string[];
};

/** Additional properties describing a tool to clients (per MCP spec @ https://modelcontextprotocol.io/specification/2025-06-18/schema#toolannotations) */
type McpToolAnnotations = {
  /** Human-readable title for the tool, useful for UI display */
  title?: string;
  /** If true, the tool does not modify its environment */
  readOnlyHint?: boolean;
  /** If true, the tool may perform destructive updates (only meaningful when readOnlyHint is false; default true) */
  destructiveHint?: boolean;
  /** If true, repeated calls with the same arguments have no additional effect (only meaningful when readOnlyHint is false; default false) */
  idempotentHint?: boolean;
  /** If true, the tool interacts with an "open world" of external entities (default true) */
  openWorldHint?: boolean;
};

/** An MCP tool describe response (per MCP spec @ https://modelcontextprotocol.io/specification/2025-06-18/server/tools#tool) */
export type McpTool = {
  /** Unique identifier for the tool */
  name: string;
  /** Optional human-readable name of the tool for display purposes */
  title?: string;
  /** Human-readable description of functionality */
  description: string;
  /** JSON Schema defining expected parameters */
  inputSchema: McpToolInputSchema;
  /** Optional JSON Schema defining expected output structure */
  outputSchema?: McpToolOutputSchema;
  /** Optional properties describing tool behavior */
  annotations?: McpToolAnnotations;
};

/** A server realized by launching a Docker container within the gateway */
export type McpContainerServer = {
  /** This is a MCP server running in a Docker container */
  type: "container";
  /** MCP transport the container speaks. `stdio` (the default when omitted) is
   * bridged to HTTP by the gateway's container-server; `http` is proxied
   * directly. Transport is an attribute of a container, not a separate server
   * type. */
  transport?: "http" | "stdio";
  /** Partial values from Container Runtime Interface v0.33.1
   *
   * Args, command, and env may template values using the nunjucks templating language
   *
   * Template variables inclue:
   * - credential: Credential string / object (depends on identity scheme)
   * - request: Request object
   *
   * Credential identity schemes:
   * - type = wif: Depends on CSP
   *   - csp = aws: Object with
   *       - AWS_ACCESS_KEY_ID
   *       - AWS_SECRET_ACCESS_KEY
   *       - AWS_SESSION_TOKEN
   *   - csp = gcp: base-64-encoded GCP-signed OIDC token
   */
  container: {
    /** Entrypoint as single-element array, or empty array for default entrypoint */
    command?: string[];
    /** Arguments passed to the container entrypoint */
    args?: string[];
    /** Environment variables */
    envs?: Record<string, string>;
    /** Container image tag */
    image: { image: string };
  };
  /** Describes the location where access is provisioned / calls are made */
  location: {
    /** The option key used in access requests */
    key: string;
    /** Agent-consumable description of the value to pass for the location in access / proxied tool calls */
    description: string;
  };
};

/** An MCP server hosted outside the gateway, reachable at a fixed URL */
export type McpExternalServer = {
  /** This is an MCP server hosted outside the gateway */
  type: "external";
  /** URL of the externally hosted MCP server */
  url: string;
};

export type McpInternalServer = {
  /** This is a built-in MCP server */
  type: "internal";
  /** The specific server built-in */
  builtin: "a2a" | "p0";
};

/** MCP server definition without tools */
type UnhydratedMcpServerDefinition = AgenticServerBase & {
  /** Defines how access is requested */
  access: {
    /** Prompt sent to the agent to make access requests */
    prompt: string;
    /** Arguments that are prepended to arguments sent by the agent */
    prefixArgs: string[];
    /** Prompt indicating how long to expect to wait after access is granted before
     *  tools will be available. Leave empty if access is immediately available.
     */
    propagationHint?: string;
    /** Request variants for servers serving more than one access type, keyed by
     *  access type. When present, the gateway's `access` tool takes a required
     *  `access_type` argument naming one of these keys. When absent, `prefixArgs`
     *  alone fully specifies the single access type this server serves.
     */
    accessTypes?: Record<string, AccessTypeSpec>;
  };
  /** Defines how the gateway evaluates authorization.
   *
   * Defaults to "upstream".
   */
  authorization: /** Access is filtered by entitlements matching tool annotations */
    | "annotation"
    /** Access is filtered by tool name */
    | "tool"
    /** Authorization is delegated to the upstream server */
    | "upstream";
  /** Defines listable fields that can be used for access */
  list: {
    /** Possible listable fields
     *
     * When `accessTypes` is present this is the union of every access type's
     * fields, so that it remains a faithful summary of what the server can list.
     */
    fields: Record<string, AccessFieldSpec>;
    /** Arguments that are prepended to arguments sent by the agent */
    prefixArgs: string[];
    /** Listable fields per access type, for servers serving more than one. Keys
     *  must match `access.accessTypes`. When present, the gateway's `list` tool
     *  takes a required `access_type` argument and only that access type's fields
     *  may be listed.
     */
    accessTypes?: Record<string, ListAccessTypeSpec>;
  };
} & (McpContainerServer | McpExternalServer | McpInternalServer); // Union over the supported hosting paradigms

type McpServerTools = {
  /** Transparent pass-through of McpTool definitions (per MCP spec) hosted by the server */
  tools: McpTool[];
};

/** Tools are manually listed */
type ManualToolHydration = {
  type: "manual";
  /** Tools are manually listed */
  tools: McpTool[];
};
/** Tools are listed using the Docker MCP registry */
type DockerToolHydration = {
  type: "docker";
  /** The identifier of this container on DockerHub */
  id: string;
};
/** Tools are listed using an upstream MCP HTTP server */
type WebToolHydration = {
  type: "web";
  /** An upstream HTTP MCP server for tool listing */
  url: string;
};
type McpToolHydration = {
  tools: DockerToolHydration | ManualToolHydration | WebToolHydration;
};

/** Server-spec fields that are P0-internal and must never reach the gateway
 * wire; {@link McpServerDefinition} omits them, and `convertServerSpec`
 * (`packages/integrations/agentic/backend/src/server/convert.ts`) strips them when
 * building one from a {@link McpServerSpec}. */
export type InternalOnlyMcpServerFields = {
  /** Tool name -> IAM permission(s) it exercises, for IAM-grounded risk
   * resolution against the IAM Risk Catalog. A tool present with `[]` is
   * confirmed to need none; a tool absent from the map is unclassified.
   * Present only on servers backed by a cloud IAM credential. */
  toolPermissions?: Record<string, string[]>;
  /** Displayed when a user selects this server; not sent to the gateway */
  iconUrl?: string;
  /** The value the installer asks for, put into `{instance}` in an external
   * server's `url`. A server without it takes no instance. */
  instance?: McpServerInstance;
};

/** A value that each install of an external server gives, such as a tenant
 * subdomain or an account id in the URL path */
export type McpServerInstance = {
  label: string;
  description: string;
  /** Must match the whole value */
  pattern: RegExp;
  placeholder?: string;
};

export type McpServerSpec = UnhydratedMcpServerDefinition &
  McpToolHydration &
  InternalOnlyMcpServerFields;

/** Returns a propagation hint for use in describing MCP tools */
export const propagationHint = (labels: {
  resource: string;
  normalWait: string;
  maxWait: string;
  pollInterval: string;
}) => `After access is granted, tool calls may fail with missing permission errors while
${labels.resource} authorization propagates. This typically takes ${labels.normalWait}, but can take up to
${labels.maxWait}. Attempt to call your tool for up to ${labels.maxWait}, spacing attempts once every ${labels.pollInterval}.

If you still encounter permission errors after ${labels.maxWait} then either:
- Your tool call requires permissions not granted by your access request
- Your request was not fully provisioned, or has been revoked (make sure the "check" tool indicates that access is granted).
  If the request status is REVOKED and "revocationDetails" is present, it identifies who revoked the access;
  report that attribution rather than speculating about the cause. If it is absent, state that the revocation
  is unattributed
- The agentic server gateway has a bug (bugs should be reported to support@p0.dev)`;

/** A gateway-deployed MCP server configuration */
export type McpServerDefinition = UnhydratedMcpServerDefinition &
  McpServerTools;
